import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Colors are shared with mobile via @learnzapp/core/theme (single source of
// truth). getColorTheme returns the shared-group web theme (semantic tokens).
import { getColorTheme } from "@learnzapp/core/theme";
import { DEFAULT_THEME_MODE, BRAND_THEME } from "../utils/brandFlags";


export const ThemeContext = createContext();

// Semantic CSS-variable map (Step 3/B). Derived from the ASSEMBLED theme object so it is
// brand-aware (default + TrainingCamp) and covers both modes; Tailwind's semantic color
// utilities (bg-primary, text-content, …) resolve through these vars. Additive only.
// Scale stops backing the Tailwind primary-50..950 utilities. Keep in lock-step with the
// primary-N keys generated in apps/web/tailwind.config.js.
const PRIMARY_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

const cssVarsFromTheme = (t) => ({
  "--color-primary": t?.primary?.blue?.[500],
  "--color-primary-hover": t?.primary?.blue?.[600],
  "--color-accent": t?.primary?.orange?.[500],
  "--color-canvas": t?.backgrounds?.primary,
  "--color-surface": t?.backgrounds?.secondary,
  "--color-content": t?.text?.primary,
  "--color-content-muted": t?.text?.secondary,
  "--color-content-subtle": t?.text?.tertiary,
  "--color-line": t?.borders?.primary,
  "--color-success": t?.semantic?.success,
  "--color-error": t?.semantic?.error,
  "--color-warning": t?.semantic?.warning,
  "--color-info": t?.semantic?.info,
});

// New users (no saved `Theme`) get the brand default (white-label brands set DEFAULT_THEME_MODE,
// e.g. Training Camp = 'dark'); the trailing 'dark' preserves the prior default for brands that
// don't set it. A user who has toggled keeps their saved choice.
const resolveInitialTheme = () =>
  localStorage.getItem("Theme") || DEFAULT_THEME_MODE || "dark";

export const ThemeProvider = (props) => {
  // Lazy-init from the resolved default so there's no light-mode flash before the effect runs.
  const [currentTheme, setCurrentTheme] = useState(resolveInitialTheme);
  useEffect(() => {}, [localStorage.getItem("SAVED_BUNDLE_DETAILS")]);
  const currentThemeRef = useRef(null);
  currentThemeRef.current = currentTheme;

  // Apply the body `dark` class before first paint (covers Tailwind dark: variants).
  useLayoutEffect(() => {
    document.body.classList.toggle("dark", resolveInitialTheme() === "dark");
  }, []);

  // Publish semantic CSS variables on :root for the current mode/brand (Step 3/B).
  // Re-applied whenever the mode changes; additive to the existing `dark` class + context.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const vars = cssVarsFromTheme(getTheme());
    // Fixed brand-blue ramp backing the Tailwind primary-50..950 utilities (replaces the retired
    // lz-blue build-time ramp). Mode-INDEPENDENT — always the LIGHT scale — because the dark scale
    // inverts (blue[100] dark = near-black), which would flip decorative `dark:*-primary-N` shades
    // to unreadable. Matches how the old lz-blue-N ramp behaved (light literals in both modes).
    const lightBlue = getColorTheme("light", "web", BRAND_THEME).primary?.blue || {};
    for (const s of PRIMARY_STOPS) vars[`--color-primary-${s}`] = lightBlue[s];
    for (const [name, value] of Object.entries(vars)) {
      if (value) root.style.setProperty(name, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTheme]);

  useEffect(() => {
    const theme = localStorage.getItem("Theme") || DEFAULT_THEME_MODE || "dark";
    const body = document.body;
    if (theme === "light") {
      setCurrentTheme("light");
      body.classList.remove("dark");
    } else if (theme === "dark") {
      setCurrentTheme("dark");
      body.classList.add("dark");
    }
    getTheme();
  }, [currentTheme]);

  async function setAppTheme(mode) {
    setCurrentTheme(mode);
    // UPDATE_USER_THEME(mode);
  }

  function getThemeName() {
    
    return currentTheme;
  }

  function getTheme() {
    // Only "dark" selects the dark theme; "system" (until OS detection lands),
    // "light", and anything unexpected resolve to light — same mapping as before.
    const mode = currentTheme === "dark" ? "dark" : "light";
    return getColorTheme(mode, "web", BRAND_THEME);
  }

  useEffect(() => {}, [currentTheme]);

  const theme = getTheme();
  return (
    <ThemeContext.Provider
      value={{
        theme,
        currentTheme,
        setCurrentTheme,
        setAppTheme,
        getThemeName,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};
